import React, { useState, useEffect } from "react";
import { adminApi, formatUSD, formatLBP } from "@/lib/api";
import { toast } from "sonner";
import { Search, RefreshCw } from "lucide-react";

export default function HistoryPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [currencyFilter, setCurrencyFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const loadHistory = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.get("/admin/audit-explorer", { params: { limit: 200 } });
      setTransactions(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "تعذر تحميل سجل المعاملات");
      toast.error("فشل تحميل سجل المعاملات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const filtered = transactions.filter((tx) => {
    const matchesSearch =
      (tx.user_username || "").toLowerCase().includes(search.toLowerCase()) ||
      (tx.user_display_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (tx.note || "").toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === "ALL" || tx.type === typeFilter;
    const matchesCurrency = currencyFilter === 'ALL' || tx.currency === currencyFilter;
    const matchesStatus = statusFilter === 'ALL' || tx.status === statusFilter;
    const date = String(tx.created_at || '').slice(0, 10);
    return matchesSearch && matchesType && matchesCurrency && matchesStatus && (!dateFrom || date >= dateFrom) && (!dateTo || date <= dateTo);
  });

  return (
    <div className="space-y-6 fade-up">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="section-title text-2xl font-black">سجل المعاملات البنكية</h2>
          <p className="text-xs text-[var(--c-muted)] mt-1">عرض وتدقيق كافة المعاملات والتحويلات المالية المسجلة.</p>
        </div>
        <button onClick={loadHistory} className="btn-navy px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2">
          <RefreshCw className="w-3.5 h-3.5" />
          <span>تحديث السجل</span>
        </button>
      </div>

      <div className="bg-white border border-[var(--c-border)] rounded-2xl p-4 shadow-sm grid gap-3 lg:grid-cols-[minmax(16rem,1fr)_auto] items-end">
        <div className="relative min-w-0">
          <Search className="w-4 h-4 text-[var(--c-muted)] absolute start-3 top-1/2 -translate-y-1/2" />
          <input
            className="input-pro ps-9"
            placeholder="بحث بالاسم أو اسم المستخدم أو البيان..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 min-w-0">
          <select className="input-pro text-xs font-bold col-span-2 sm:col-span-1" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="ALL">جميع أنواع المعاملات</option>
            <option value="deposit">إيداع</option>
            <option value="withdraw">سحب</option>
            <option value="transfer">تحويل عملاء</option>
            <option value="currency_exchange">تحويل عملات</option>
          </select>
          <select className="input-pro text-xs font-bold" value={currencyFilter} onChange={e=>setCurrencyFilter(e.target.value)}><option value="ALL">كل العملات</option><option value="USD">USD</option><option value="LBP">LBP</option></select>
          <select className="input-pro text-xs font-bold" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}><option value="ALL">كل الحالات</option><option value="POSTED">مكتملة</option><option value="REVERSED">معكوسة</option></select>
          <input type="date" className="input-pro text-xs" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} aria-label="من تاريخ" />
          <input type="date" className="input-pro text-xs" value={dateTo} onChange={e=>setDateTo(e.target.value)} aria-label="إلى تاريخ" />
        </div>
      </div>

      <div className="bg-white border border-[var(--c-border)] rounded-2xl shadow-sm overflow-hidden">
        {error ? (
          <div className="p-8 text-center space-y-3" role="alert"><p className="text-sm font-bold text-red-700">{error}</p><button onClick={loadHistory} className="btn-navy">إعادة المحاولة</button></div>
        ) : loading ? (
          <div className="p-8 text-center text-xs text-[var(--c-muted)]">جاري تحميل السجل...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-xs text-[var(--c-muted)]">لا توجد معاملات مطابقة للبحث</div>
        ) : (
          <>
          <div className="hidden md:block mb-table-wrap">
            <table className="w-full text-end text-xs sm:text-sm">
              <thead className="bg-slate-50 border-b border-[var(--c-border)] text-[var(--c-muted)] font-bold">
                <tr>
                  <th className="p-4 text-start">التاريخ والوقت</th>
                  <th className="p-4">المستخدم</th>
                  <th className="p-4">نوع المعاملة</th>
                  <th className="p-4">المبلغ</th>
                  <th className="p-4">البيان</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((tx) => (
                  <tr key={tx.id} onClick={()=>setSelected(tx)} className="hover:bg-slate-50/80 transition cursor-pointer">
                    <td className="p-4 text-start font-num text-xs text-[var(--c-muted)]">
                      {new Date(tx.created_at).toLocaleString("ar-LB")}
                    </td>
                    <td className="p-4 font-bold text-[var(--c-navy)]">
                      {tx.user_display_name} <span className="text-xs text-[var(--c-muted)] font-normal">(@{tx.user_username})</span>
                    </td>
                    <td className="p-4 font-bold">
                      <span className="px-2.5 py-1 rounded-lg bg-slate-100 border text-xs">
                        {tx.type === "deposit" ? "إيداع" : tx.type === "withdraw" ? "سحب" : tx.type === "transfer" ? "تحويل" : tx.type}
                      </span>
                    </td>
                    <td className="p-4 font-num font-bold text-[var(--c-navy)]">
                      {tx.currency === "USD" ? `$ ${formatUSD(tx.amount)}` : `${formatLBP(tx.amount)} ل.ل`}
                    </td>
                    <td className="p-4 text-xs text-[var(--c-muted)]">{tx.transaction?.note || tx.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-[var(--c-border)]">
            {filtered.map(tx => <button key={tx.id} onClick={()=>setSelected(tx)} className="w-full p-4 text-start hover:bg-[var(--c-cream)]/60 transition">
              <div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="font-bold break-words text-[var(--c-navy)]">{tx.user_display_name || tx.user_username || '—'}</div><div className="mt-1 text-[10px] text-[var(--c-muted)]">{new Date(tx.created_at).toLocaleString('ar-LB')}</div></div><div className="shrink-0 text-end font-num font-black text-sm text-[var(--c-navy)]">{tx.currency === 'USD' ? `$ ${formatUSD(tx.amount)}` : `${formatLBP(tx.amount)} ل.ل`}</div></div><div className="mt-3 flex items-center justify-between gap-3"><span className="rounded-lg border bg-[var(--color-surface-secondary)] px-2 py-1 text-[10px] font-bold">{tx.type === 'deposit' ? 'إيداع' : tx.type === 'withdraw' ? 'سحب' : tx.type === 'transfer' ? 'تحويل' : tx.type}</span><span className="min-w-0 truncate text-[10px] text-[var(--c-muted)]">{tx.note || '—'}</span></div>
            </button>)}
          </div>
          </>
        )}
      </div>
      {selected && <div className="fixed inset-0 z-50 bg-black/40 p-3 sm:p-4 flex items-center justify-center" onClick={()=>setSelected(null)}><div className="bg-white rounded-2xl p-5 w-full max-w-lg max-h-[calc(100dvh-1.5rem)] overflow-y-auto space-y-3" onClick={e=>e.stopPropagation()} role="dialog" aria-modal="true"><div className="flex justify-between"><h3 className="font-bold">تفاصيل التدقيق</h3><button className="w-10 h-10" onClick={()=>setSelected(null)}>×</button></div><AuditRow label="المستخدم" value={selected.display_name||selected.username||'حساب النظام'}/><AuditRow label="الفاعل" value={selected.actor?.name||'النظام'}/><AuditRow label="النوع والحالة" value={`${selected.type} · ${selected.status}`}/><AuditRow label="المبلغ" value={`${selected.change>=0?'+':'−'}${selected.amount} ${selected.currency}`}/><AuditRow label="الرصيد" value={selected.balance_before===null?'غير متاح للسجلات القديمة':`${selected.balance_before} → ${selected.balance_after} ${selected.currency}`}/><AuditRow label="التاريخ" value={new Date(selected.created_at).toLocaleString('ar-LB')}/></div></div>}
    </div>
  );
}
function AuditRow({label,value}) { return <div className="flex justify-between gap-4 border-b pb-2 text-sm"><span className="text-[var(--c-muted)]">{label}</span><b className="text-end">{value}</b></div>; }
